import axios, { AxiosError, AxiosInstance } from 'axios';
import { ProductCategory } from './product-category.service';

export interface ProductTag {
    id: number;
    title: string;
}

export interface Product {
    id: string;
    name: string;
    latin_name: string;
    synonym: string;
    familia: string;
    part_used: string;
    method_of_reproduction: string;
    harvest_age: string;
    morphology: string;
    area_name: string;
    efficacy: string;
    utilization: { values: string[] } | null;
    composition: { values: string[] } | null;
    images: Array<{
        id: number;
        image: string;
    }>;
    tag: ProductTag[];
    research_results: string;
    description: string;
    price: number;
    unit_type: string;
    product_category_id: number;
    product_category?: ProductCategory;
    category_name?: string;
    created_at: string;
    product_category_name: string;
    updated_at: string;
}

export interface CreateProductData {
    name?: string;
    latin_name?: string;
    synonym?: string;
    familia?: string;
    part_used?: string;
    method_of_reproduction?: string;
    harvest_age?: string;
    morphology?: string;
    area_name?: string;
    efficacy?: string;
    utilization?: string[];
    composition?: string[];
    images?: File[];
    tag?: string[];
    research_results?: string;
    description?: string;
    price?: number;
    unit_type?: string;
    product_category_id?: number;
}

export interface UpdateProductData{
    id: string;
    name?: string;
    latin_name?: string;
    synonym?: string;
    familia?: string;
    part_used?: string;
    method_of_reproduction?: string;
    harvest_age?: string;
    morphology?: string;
    area_name?: string;
    efficacy?: string;
    utilization?: string[];
    composition?: string[];
    images?: File[];
    research_results?: string;
    description?: string;
    price?: number;
    unit_type?: string;
    product_category_id?: number;
}

class ProductService {
    private axiosInstance: AxiosInstance;

    constructor() {
        this.axiosInstance = axios.create({
            baseURL: process.env.REACT_APP_API_URL,
            timeout: 10000,
        });

        this.axiosInstance.interceptors.request.use(
            (config) => {
                const storageType = localStorage.getItem('storageType');
                const storage = storageType === 'local' ? localStorage : sessionStorage;
                const token = storage.getItem('token');
                if (token && config.headers) {
                    config.headers.Authorization = `Bearer ${token}`;
                }
                return config;
            },
            (error) => Promise.reject(error)
        );
    }

    async createProduct(data: CreateProductData): Promise<Product | undefined> {
        try {
            const formData = new FormData();
            
            Object.entries(data).forEach(([key, value]) => {
                if (key === 'utilization') {
                    if (value) {
                        formData.append(key, JSON.stringify({ values: value }));
                    }
                } else if (key === 'composition') {
                    if (value) {
                        formData.append(key, JSON.stringify({ values: value }));
                    }
                } else if (key === 'images') {
                    const imageFiles = value as File[];
                    if (imageFiles && imageFiles.length > 0) {
                        const limitedImages = imageFiles.slice(0, 10);
                        limitedImages.forEach((file, index) => {
                            formData.append(`images`, file, `image_${index + 1}`);
                        });
                    }
                } else if (key === 'tag' && Array.isArray(value)) {
                    formData.append('tag', value.join(', '));
                } else if (value !== undefined && value !== null) {
                    formData.append(key, value.toString());
                }
            });
    
            const response = await this.axiosInstance.post<{
                message: string;
                Product: Product;
            }>('/admin/product', formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });
    
            console.log('Product created:', response);
            return response.data.Product;
        } catch (error) {
            this.handleError(error, 'Failed to create product');
            return undefined;
        }
    }

    async getProduct(id: string): Promise<Product | undefined> {
        try {
            const response = await this.axiosInstance.get<{
                data: Product;
            }>(`/product/${id}`);
            console.log(response.data);
            
            return response.data.data;
        } catch (error) {
            this.handleError(error, 'Failed to retrieve product');
            return undefined;
        }
    }

    async updateProduct(data: UpdateProductData): Promise<Product | undefined> {
        try {
            const formData = new FormData();
            Object.entries(data).forEach(([key, value]) => {
                if (key === 'utilization' || key === 'composition') {
                    if (value) {
                        formData.append(key, JSON.stringify({ values: value }));
                    }
                } else if (key === 'images') {
                    const files = value as File[];
                    files.forEach(file => {
                        formData.append('images', file);
                    });
                } else if (value !== undefined && value !== null) {
                    formData.append(key, value.toString());
                }
            });
    
            const response = await this.axiosInstance.put<{
                message: string;
                Product: Product;
            }>(`/admin/product/${data.id}`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });
    
            return response.data.Product;
    
        } catch (error) {
            if (error instanceof AxiosError && error.response?.status === 409) {
                throw new Error("Conflict: A product with this data already exists.");
            }
            this.handleError(error, 'Failed to update product');
            return undefined;
        }
    }

    async deleteProduct(id: string): Promise<void> {
        try {
            await this.axiosInstance.delete(`/admin/product/${id}`);
        } catch (error) {
            this.handleError(error, 'Failed to delete product');
        }
    }

    async getAllProducts() {
        try {
            const response = await this.axiosInstance.get<{
                data: Product[];
            }>('/product');
            console.log(response.data.data);

            return response.data.data;
        } catch (error) {
            this.handleError(error, 'Failed to retrieve products');
            return [];
        }
    }

    async getProductsByName(name: string): Promise<{ Products: Product[] }> {
        try {
            const response = await this.axiosInstance.get<{
                data: Product[];
            }>('/product/get-name', {
                params: { name }
            });
            console.log(response.data);
            
            return { Products: response.data.data };
        } catch (error) {
            this.handleError(error, 'Failed to search products by name');
            return { Products: [] };
        }
    }

    async deleteProductGalleryImages(productId: number, imageIds: number[]): Promise<Product | undefined> {
        try {
            const response = await this.axiosInstance.put<{
                message: string;
                data: Product;
            }>(`/admin/product/${productId}?image-delete=${imageIds.join(',')}`, {}, {
                headers: { 'Content-Type': 'application/json' },
            });

            return response.data.data;
        } catch (error) {
            this.handleError(error, 'Failed to delete gallery images');
            return undefined;
        }
    }

    async deleteProductTags(productId: string, tagIds: number[]): Promise<Product | undefined> {
        try {
            const response = await this.axiosInstance.put<{
                message: string;
                data: Product;
            }>(`/admin/product/${productId}?tag-delete=${tagIds.join(',')}`, {}, {
                headers: { 'Content-Type': 'application/json' },
            });

            return response.data.data;
        } catch (error) {
            this.handleError(error, 'Failed to delete product tags');
            return undefined;
        }
    }

    async getAllProductsWithCategories(): Promise<Product[]> {
        try {
            const [productsResponse, categoriesResponse] = await Promise.all([
                this.getAllProducts(),
                this.axiosInstance.get<{ data: ProductCategory[] }>('/product-category'),
            ]);

            const products = productsResponse;
            const categories = categoriesResponse.data.data;
            const categoryMap = new Map(categories.map(cat => [cat.id, cat.name]));

            return products.map(product => ({
                ...product,
                category_name: categoryMap.get(product.product_category_id) || '',
            }));
        } catch (error) {
            this.handleError(error, 'Failed to retrieve products with categories');
            return [];
        }
    }

    async getProductById(id: string): Promise<Product | undefined> {
        try {
            const response = await this.axiosInstance.get<{
                data: Product;
            }>(`/product/${id}`);
            const product = response.data.data;
            
            if (product && product.product_category_id) {
                const categoryName = await this.getCategoryName(product.product_category_id);
                return {
                    ...product,
                    category_name: categoryName
                };
            }
            
            return product;
        } catch (error) {
            this.handleError(error, 'Failed to retrieve product details');
            return undefined;
        }
    }

    private async getCategoryName(categoryId: number): Promise<string> {
        try {
            const response = await this.axiosInstance.get<{
                data: ProductCategory[];
            }>('/product-category');
            const categories = response.data.data;
            const category = categories.find(cat => cat.id === categoryId);
            return category ? category.name : '';
        } catch (error) {
            throw new Error('Failed to fetch categories');
        }
    }
    async deleteProductAssociations(
        productId: string,
        imageIds?: number[],
        tagIds?: number[]
    ): Promise<Product | undefined> {
        try {
            
            // Build query parameters
            const queryParams = [];
            if (imageIds && imageIds.length > 0) {
                queryParams.push(`image-delete=${imageIds.join(',')}`);
            }
            if (tagIds && tagIds.length > 0) {
                queryParams.push(`tag-delete=${tagIds.join(',')}`);
            }

            // Construct URL with query parameters
            const url = `/admin/product/${productId}${
                queryParams.length ? '?' + queryParams.join('&') : ''
            }`;

            const response = await this.axiosInstance.put<{
                message: string;
                data: Product;
            }>(url, {}, {
                headers: { 'Content-Type': 'application/json' },
            });

            return response.data.data;
        } catch (error) {
            this.handleError(error, 'Failed to delete product associations');
            return undefined;
        }
    }

    private handleError(error: unknown, message: string) {
        if (error instanceof AxiosError) {
            const backendMsg = error.response?.data?.error || error.response?.data?.message || JSON.stringify(error.response?.data);
            const details = error.response?.data?.details;
            let fullMsg = backendMsg || message;
            if (details) fullMsg += ` (${JSON.stringify(details)})`;
            console.error(`${message}:`, fullMsg);
            throw new Error(fullMsg);
        }
        console.error(message, error);
        const errorMsg = error instanceof Error ? error.message : String(error);
        throw new Error(`${message}: ${errorMsg}`);
    }
}

export const productService = new ProductService();